import { useState, useEffect } from 'react';
import { hrms, getFileUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';

const ROLE_COLORS = {
  super_admin:       '#7C3AED',
  hr_admin:          '#0369A1',
  hr_head:           '#0369A1',
  hr_executive:      '#0284C7',
  it_admin:          '#0F766E',
  finance_head:      '#B45309',
  finance_team:      '#D97706',
  cto:               '#1E40AF',
  ceo:               '#9D174D',
  project_manager:   '#0F766E',
  team_lead:         '#4338CA',
  reporting_manager: '#1D4ED8',
  employee:          '#374151',
};
const rc = (role) => ROLE_COLORS[role] || '#374151';

// ── Avatar (stable fallback) ──────────────────────────────────────────────────
function Avatar({ photo, name, size = 52 }) {
  const [failed, setFailed] = useState(false);
  const COLORS = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#0EA5E9', '#F59E0B'];
  const color    = COLORS[(name || '').charCodeAt(0) % COLORS.length];
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const url      = photo ? getFileUrl(photo) : null;

  if (url && !failed) {
    return (
      <img src={url} alt={name} onError={() => setFailed(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover',
                 border: '3px solid rgba(255,255,255,0.6)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                 flexShrink: 0 }} />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: size * 0.33, fontWeight: 700, color: '#fff', flexShrink: 0,
                  border: '3px solid rgba(255,255,255,0.6)', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
      {initials}
    </div>
  );
}

// ── Chain card (manager / CEO) ────────────────────────────────────────────────
function ChainCard({ person, label }) {
  const color = rc(person.role);
  return (
    <div style={{ position: 'relative', background: '#fff', border: `2px solid ${color}55`,
                  borderRadius: 14, padding: '1rem 1.25rem', minWidth: 260, maxWidth: 320,
                  display: 'flex', alignItems: 'center', gap: '0.875rem',
                  boxShadow: `0 2px 0 ${color}20, 0 6px 16px rgba(0,0,0,0.07)` }}>
      {label && (
        <span style={{ position: 'absolute', top: -11, left: 14,
                       background: color, color: '#fff', fontSize: '0.6rem', fontWeight: 800,
                       padding: '0.18rem 0.55rem', borderRadius: 9999,
                       textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </span>
      )}
      <Avatar photo={person.profile_photo} name={person.name} size={48} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
        <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: theme.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {person.name}
        </p>
        {person.emp_code && (
          <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: color }}>
            #{person.emp_code}
          </p>
        )}
        {person.designation && (
          <span style={{ display: 'inline-block', alignSelf: 'flex-start', background: color,
                         color: '#fff', fontSize: '0.62rem', fontWeight: 700,
                         padding: '0.12rem 0.5rem', borderRadius: 9999,
                         maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {person.designation}
          </span>
        )}
        {person.department && (
          <p style={{ margin: 0, fontSize: '0.68rem', color: theme.textMuted,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {person.department}
          </p>
        )}
      </div>
    </div>
  );
}

// ── "YOU" card ────────────────────────────────────────────────────────────────
function YouCard({ person }) {
  const color = rc(person.role);
  return (
    <div style={{ position: 'relative', background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                  borderRadius: 16, padding: '1.25rem 1.5rem', minWidth: 280, maxWidth: 340,
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  boxShadow: `0 4px 0 ${color}60, 0 8px 0 ${color}30, 0 12px 28px ${color}40` }}>
      <span style={{ position: 'absolute', top: -11, left: 16,
                     background: '#fff', color: color, fontSize: '0.62rem', fontWeight: 800,
                     padding: '0.18rem 0.6rem', borderRadius: 9999,
                     textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        YOU
      </span>
      <Avatar photo={person.profile_photo} name={person.name} size={60} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#fff',
                    wordBreak: 'break-word' }}>
          {person.name}
        </p>
        {person.emp_code && (
          <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>
            #{person.emp_code}
          </p>
        )}
        {person.designation && (
          <span style={{ display: 'inline-block', alignSelf: 'flex-start',
                         background: 'rgba(255,255,255,0.22)', color: '#fff',
                         fontSize: '0.65rem', fontWeight: 700,
                         padding: '0.15rem 0.55rem', borderRadius: 9999,
                         maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {person.designation}
          </span>
        )}
        {person.department && (
          <p style={{ margin: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.75)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {person.department}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Direct report mini card ───────────────────────────────────────────────────
function ReportCard({ person }) {
  const [hovered, setHovered] = useState(false);
  const color = rc(person.role);
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: hovered ? `${color}08` : '#fff',
               border: `1.5px solid ${hovered ? color : color + '30'}`,
               borderLeft: `3.5px solid ${color}`,
               borderRadius: 10, padding: '0.75rem 0.875rem',
               display: 'flex', alignItems: 'center', gap: '0.7rem',
               transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
               boxShadow: hovered ? `0 4px 12px ${color}20` : '0 1px 4px rgba(0,0,0,0.06)',
               width: '100%' }}>
      <Avatar photo={person.profile_photo} name={person.name} size={40} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
        <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 700, color: theme.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {person.name}
        </p>
        {person.emp_code && (
          <p style={{ margin: 0, fontSize: '0.68rem', fontWeight: 700, color: color }}>
            #{person.emp_code}
          </p>
        )}
        {person.designation && (
          <span style={{ display: 'inline-block', alignSelf: 'flex-start',
                         background: color, color: '#fff', fontSize: '0.6rem', fontWeight: 700,
                         padding: '0.1rem 0.45rem', borderRadius: 9999,
                         maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {person.designation}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Peer mini card ────────────────────────────────────────────────────────────
function PeerCard({ person }) {
  const color = rc(person.role);
  return (
    <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 10,
                  padding: '0.65rem 0.875rem', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
      <Avatar photo={person.profile_photo} name={person.name} size={36} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
        <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 700, color: theme.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {person.name}
        </p>
        {person.designation && (
          <span style={{ display: 'inline-block', alignSelf: 'flex-start',
                         background: color + '18', color: color, fontSize: '0.6rem', fontWeight: 700,
                         padding: '0.08rem 0.4rem', borderRadius: 9999,
                         maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {person.designation}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Connector ─────────────────────────────────────────────────────────────────
function Connector({ label }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: 2, height: 16, background: '#D1D5DB' }} />
      <span style={{ fontSize: '0.68rem', color: theme.textMuted, background: '#F3F4F6',
                     border: `1px solid ${theme.border}`, borderRadius: 9999,
                     padding: '0.1rem 0.55rem', margin: '2px 0' }}>
        {label || 'reports to ↓'}
      </span>
      <div style={{ width: 2, height: 16, background: '#D1D5DB' }} />
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
function MyOrgChart() {
  const { user }              = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hrms.getMyOrgPath().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading…</div>
  );

  if (!data?.path?.length) return (
    <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>
      <p style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏢</p>
      <p>No org chart data available. Please contact HR to assign your reporting manager.</p>
    </div>
  );

  const { path, direct_reports = [], peers = [] } = data;
  // path[0] = me, path[1] = direct manager, ..., path[last] = top
  // Override profile_photo for the YOU card with the auth-context value —
  // it's already loaded at login and uses the same getFileUrl path as the navbar.
  const me    = { ...path[0], profile_photo: user?.profile_photo ?? path[0].profile_photo };
  const chain = [...path.slice(1)].reverse(); // top → direct manager

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>My Organization Chart</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
          Your position in the organization hierarchy
        </p>
      </div>

      {/* ── Hierarchy column ── */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>

        {/* Chain: top → direct manager */}
        {chain.map((person, idx) => (
          <div key={person.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ChainCard
              person={person}
              label={idx === 0 ? (person.designation || person.role?.replace(/_/g, ' ') || 'Top Level') : null}
            />
            <Connector label="reports to ↓" />
          </div>
        ))}

        {/* YOU */}
        <YouCard person={me} />

        {/* Direct reports */}
        {direct_reports.length > 0 && (
          <>
            <div style={{ width: 2, height: 20, background: '#D1D5DB' }} />
            <div style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 700,
                          background: '#F3F4F6', border: `1px solid ${theme.border}`,
                          borderRadius: 9999, padding: '0.15rem 0.75rem', marginBottom: '1rem' }}>
              {direct_reports.length} Direct Report{direct_reports.length !== 1 ? 's' : ''}
            </div>

            {/* Grid: 3 columns on wide, 2 on medium */}
            <div style={{ display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                          gap: '0.75rem', width: '100%', maxWidth: 820 }}>
              {direct_reports.map(r => <ReportCard key={r.id} person={r} />)}
            </div>
          </>
        )}
      </div>

      {/* ── Peers ── */}
      {peers.length > 0 && (
        <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 12,
                      padding: '1.25rem 1.5rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: theme.text, margin: '0 0 0.875rem' }}>
            Colleagues — {peers.length} people share your reporting manager
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.625rem' }}>
            {peers.map(p => <PeerCard key={p.id} person={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default MyOrgChart;
